// plan-20 S01/S02: from-scratch GIF89a writer — no deps, port of the reference
// vague-ifier.html encoder. Median-cut palette, green-weighted nearest-color
// with Bayer dither, LZW, netscape infinite loop, and the hex cipher riding in
// a comment block. Pure (no DOM) so the LZW round-trip is verifiable in Node.

// the cipher — carried in the gif comment block AND the blessed.png tEXt chunk.
// the first byte pair is not a typo. do not "fix" it.
export const CIPHER = '48 49 4E 47 20 49 53 20 4C 49 53 54 45 4E 49 4E 47'

// ordered 4x4 Bayer matrix — shared by the fused pixel pass (engine) and the
// gif quantizer's dither perturbation.
export const BAYER4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
]

export interface FrameLike {
  data: Uint8ClampedArray | Uint8Array
  width: number
  height: number
}

export interface Palette {
  pal: Uint8Array
  count: number
}

// median-cut with a spread * log(size) split priority — favors both wide and
// populous boxes so the green ramp keeps its gradations.
export function medianCutPalette(samples: Uint8Array, n: number): Palette {
  const chan = (i: number, c: number): number => samples[i * 3 + c]
  const makeBox = (idx: number[]) => {
    const mn = [255, 255, 255]
    const mx = [0, 0, 0]
    for (const i of idx) {
      for (let c = 0; c < 3; c++) {
        const v = chan(i, c)
        if (v < mn[c]) mn[c] = v
        if (v > mx[c]) mx[c] = v
      }
    }
    let axis = 0
    for (let c = 1; c < 3; c++) if (mx[c] - mn[c] > mx[axis] - mn[axis]) axis = c
    return { idx, axis, spread: mx[axis] - mn[axis] }
  }
  let boxes = [makeBox(Array.from({ length: samples.length / 3 }, (_, i) => i))]
  while (boxes.length < n) {
    boxes.sort((a, b) => b.spread * Math.log(b.idx.length + 1) - a.spread * Math.log(a.idx.length + 1))
    const box = boxes[0]
    if (!box || box.spread === 0 || box.idx.length < 2) break
    box.idx.sort((a, b) => chan(a, box.axis) - chan(b, box.axis))
    const mid = box.idx.length >> 1
    boxes.splice(0, 1, makeBox(box.idx.slice(0, mid)), makeBox(box.idx.slice(mid)))
  }
  const pal = new Uint8Array(768)
  const count = Math.min(256, boxes.length)
  for (let p = 0; p < count; p++) {
    let r = 0, g = 0, b = 0
    for (const i of boxes[p].idx) { r += chan(i, 0); g += chan(i, 1); b += chan(i, 2) }
    const m = boxes[p].idx.length || 1
    pal[p * 3] = r / m
    pal[p * 3 + 1] = g / m
    pal[p * 3 + 2] = b / m
  }
  return { pal, count }
}

export function frameToIndices(imgData: FrameLike, pal: Uint8Array, palCount: number, cache: Int16Array): Uint8Array {
  const d = imgData.data
  const W = imgData.width
  const n = W * imgData.height
  const out = new Uint8Array(n)
  for (let i = 0; i < n; i++) {
    const x = i % W
    const y = (i / W) | 0
    const dth = (BAYER4[y & 3][x & 3] / 16 - 0.5) * 10
    const r = Math.max(0, Math.min(255, d[i * 4] + dth))
    const g = Math.max(0, Math.min(255, d[i * 4 + 1] + dth))
    const b = Math.max(0, Math.min(255, d[i * 4 + 2] + dth))
    const key = ((r & 0xF8) << 7) | ((g & 0xF8) << 2) | (b >> 3)
    let pi = cache[key]
    if (pi < 0) {
      let best = 0, bd = Infinity
      for (let p = 0; p < palCount; p++) {
        const dr = r - pal[p * 3], dg = g - pal[p * 3 + 1], db = b - pal[p * 3 + 2]
        const dist = dr * dr + 2 * dg * dg + db * db // the kingdom weighs green
        if (dist < bd) { bd = dist; best = p }
      }
      pi = best
      cache[key] = pi
    }
    out[i] = pi
  }
  return out
}

// GIF-flavor LZW (jsgif output structure: emit, then grow when free > maxcode).
// On table-full the clear flag is set BEFORE emitting CLEAR so the code-size
// reset fires inside that emit — the next code goes out at minCodeSize+1 bits.
// Reordering this reintroduces a decoder desync that took a round-trip harness
// to find (see BREW.md). Do not touch.
export function lzwEncode(minCodeSize: number, pixels: Uint8Array): number[] {
  const CLEAR = 1 << minCodeSize
  const EOI = CLEAR + 1
  let nBits = minCodeSize + 1
  let maxCode = (1 << nBits) - 1
  let free = EOI + 1
  let clearFlag = false
  let cur = 0, curBits = 0
  const bytes: number[] = []
  const emit = (code: number): void => {
    cur |= code << curBits
    curBits += nBits
    while (curBits >= 8) { bytes.push(cur & 255); cur >>= 8; curBits -= 8 }
    if (free > maxCode || clearFlag) {
      if (clearFlag) { nBits = minCodeSize + 1; maxCode = (1 << nBits) - 1; clearFlag = false }
      else { nBits++; maxCode = nBits === 12 ? 4096 : (1 << nBits) - 1 }
    }
  }
  let table = new Map<number, number>()
  emit(CLEAR)
  let ent = pixels[0]
  for (let i = 1; i < pixels.length; i++) {
    const c = pixels[i]
    const key = (ent << 8) | c
    const hit = table.get(key)
    if (hit !== undefined) { ent = hit; continue }
    emit(ent)
    if (free < 4096) {
      table.set(key, free++)
    } else {
      table = new Map<number, number>()
      free = EOI + 1
      clearFlag = true
      emit(CLEAR)
    }
    ent = c
  }
  emit(ent)
  emit(EOI)
  if (curBits > 0) bytes.push(cur & 255)
  return bytes
}

export function buildGif(W: number, H: number, indexFrames: Uint8Array[], pal: Uint8Array, delayCs: number): Uint8Array<ArrayBuffer> {
  const out: number[] = []
  const push8 = (b: number): void => { out.push(b & 255) }
  const push16 = (v: number): void => { out.push(v & 255, (v >> 8) & 255) }
  const pushStr = (str: string): void => { for (let i = 0; i < str.length; i++) out.push(str.charCodeAt(i)) }
  pushStr('GIF89a')
  push16(W); push16(H)
  out.push(0xF7, 0, 0) // GCT present, 256 entries
  for (let i = 0; i < 768; i++) push8(pal[i])
  out.push(0x21, 0xFF, 0x0B) // netscape: loop forever
  pushStr('NETSCAPE2.0')
  out.push(3, 1, 0, 0, 0)
  out.push(0x21, 0xFE, CIPHER.length) // the cipher rides as a comment
  pushStr(CIPHER)
  out.push(0)
  for (const idxs of indexFrames) {
    out.push(0x21, 0xF9, 4, 0x04) // GCE, disposal: do not dispose
    push16(delayCs)
    out.push(0, 0)
    out.push(0x2C, 0, 0, 0, 0) // image descriptor at (0,0)
    push16(W); push16(H)
    out.push(0)
    out.push(8) // LZW min code size
    const data = lzwEncode(8, idxs)
    for (let i = 0; i < data.length; i += 255) {
      const nn = Math.min(255, data.length - i)
      out.push(nn)
      for (let j = 0; j < nn; j++) out.push(data[i + j])
    }
    out.push(0)
  }
  out.push(0x3B)
  return new Uint8Array(out)
}
